import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Autocomplete, Box, Checkbox, Chip, FormControl, FormControlLabel, FormHelperText, InputAdornment, InputLabel, ListItemText, MenuItem, OutlinedInput, Radio, RadioGroup, Select, SelectChangeEvent, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { FunctionComponent, ReactNode, useContext, useEffect, useState } from 'react'
import VehicleService from '../../../../../shared/services/vehicle/vehicle.service';
import LocationService from '../../../../../shared/services/location/location.service';
import ProjectService from '../../../../../shared/services/project/project.service';
import ContactSupplierService from '../../../../../shared/services/contact-supplier/contact-supplier.service';
import RegulatedEmissionTradingSchemeService from '../../../../../shared/services/regulated-emission-trading-scheme/regulated-emission-trading-scheme.service';
import { ICountry, ILocation } from '../../../../../shared/services/location/location.model';
import { IProject } from '../../../../../shared/services/project/project.model';
import { IContactSupplierDetails } from '../../../../../shared/services/contact-supplier/contact-supplier.model';
import { SupplierPicker } from '../../../form-field-components/supplier-picker';
import { IRegulatedEmissionTradingScheme } from '../../../../../shared/services/regulated-emission-trading-scheme/regulated-emission-trading-scheme.model';
import { IVehicleDetails } from '../../../../../shared/services/vehicle/vehicle.model';
import { UserDetailsContext } from '../../../../../context/user-details/user-details';
import { FormikProps } from 'formik';
import { ScreenLabelsContext } from '../../../../../context/screen-labels/screen-labels';
import { ICustomField } from '../../../../../shared/services/custom-field/custom-field.model';
import { IEnergySupplierTariff } from '../../../../../shared/services/energy-supplier-tariff/energy-supplier-tariff.model';
import { IReneweableEnergyCertificate } from '../../../../../shared/services/renewable-energy-certificate/renewable-energy-certificate.model';
import RenewableEnergyCertificateService from '../../../../../shared/services/renewable-energy-certificate/renewable-energy-certificate.service';
import EnergySupplierTariffService from '../../../../../shared/services/energy-supplier-tariff/energy-supplier-tariff.service';
import { FLAG_CATEGORY_OPTIONS, FLAG_NO_CATEGORY_VALUE, flagFieldsFromSelectValue, flagSelectValueFrom } from '../../../../../shared/constants/flag-emissions';
import CapitalGoodService from '../../../../../shared/services/capital-good/capital-good-service';
import { hasSupplierRate } from '../../../../../shared/utils/supplier-rate-utils';
import { ICapitalGood } from '../../../../../shared/services/capital-good/capital-good.model';

export const EmissionsInputAdvanced: FunctionComponent<{
  isEdit?: boolean
  customFields: ICustomField[],
  formik: FormikProps<any>,
  defaultContactSupplierId?: string,
  defaultEnergySupplierTariffId?: string,
  hideCountry?: boolean,
  hideLocation?: boolean,
  hideSupplier?: boolean,
  hideVehicle?: boolean,
  hideProduct?: boolean,
  hideProject?: boolean,
  hideCustomBasedMarketMultiplier?: boolean,
  hideEnergySupplierTariff?: boolean,
  hideRec?: boolean,
  /** Which certificate types the REC selector should offer: 'gas' -> RGGO only; 'electricity' -> REC/REGO/GoO. */
  recTypeContext?: 'electricity' | 'gas',
  hideCustomFields?: boolean,
  hideRegulatedEmissionTradingScheme?: boolean,
  hideExcludedFromReporting?: boolean,
  showOnlyCustomBasedMarketMultiplier?: boolean,
  /** Field keys that should render outside (above) the Advanced collapse, as mandatory fields. */
  mandatoryFields?: string[],
  /**
   * Controls the parent form owns but wants collected in here, rendered first. The manual
   * entry form passes its subsidiary and conversion factor source: both are defaulted and
   * rarely changed, and they were sitting between the user and the record they came to write.
   */
  leadingFields?: ReactNode,
}> = ({
  customFields,
  formik,
  defaultContactSupplierId,
  defaultEnergySupplierTariffId,
  hideCountry = false,
  hideLocation = false,
  hideSupplier = false,
  hideVehicle = false,
  hideProduct = false,
  hideProject = false,
  hideCustomBasedMarketMultiplier = false,
  hideEnergySupplierTariff = false,
  hideRec = false,
  recTypeContext = 'electricity',
  hideCustomFields = false,
  hideRegulatedEmissionTradingScheme = false,
  hideExcludedFromReporting = false,
  isEdit = false,
  showOnlyCustomBasedMarketMultiplier = false,
  mandatoryFields = [],
  leadingFields,
}) => {
    //Services
    const vehicleService = VehicleService.getInstance();
    const locationService = LocationService.getInstance();
    const projectService = ProjectService.getInstance()
    const contactSupplierService = ContactSupplierService.getInstance()
    const regulatedEmissionTradingScheme = new RegulatedEmissionTradingSchemeService()
    const renewableEnergyCertificateService = new RenewableEnergyCertificateService()
    const energySupplierTariffService = new EnergySupplierTariffService()
    const capitalGoodService = new CapitalGoodService()
    const userDetailsContext = useContext(UserDetailsContext)
    const organisationDetails = userDetailsContext.userDetails?.organisation
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [viewAdvanced, setViewAdvanced] = useState<boolean>(false)
    const [locations, setLocations] = useState<ILocation[]>()
    const [contactSuppliers, setContactSuppliers] = useState<IContactSupplierDetails[]>()
    const [projects, setProjects] = useState<IProject[]>()
    const [countries, setCountries] = useState<ICountry[]>()
    const [schemes, setSchemes] = useState<IRegulatedEmissionTradingScheme[]>()
    const [showEts, setShowEts] = useState<boolean>(false)
    const [vehicles, setVehicles] = useState<IVehicleDetails[]>()
    const [capitalGoods, setCapitalGoods] = useState<ICapitalGood[]>()
    const [energySupplierTariffs, setEnergySupplierTariffs] = useState<IEnergySupplierTariff[]>()
    const [renewableEnergyCertificates, setRenewableEnergyCertificates] = useState<IReneweableEnergyCertificate[]>()
    // A supplier rate of 0 is a real value (a 100% renewable tariff), so presence is tested
    // explicitly rather than by truthiness, which read a 0 rate as "no rate".
    const [electricitySupplierMode, setElectricitySupplierMode] = useState<'precreated' | 'own'>(
      () => showOnlyCustomBasedMarketMultiplier ? 'own' : (formik.values.energySupplierTariffId ? 'precreated' : (hasSupplierRate(formik.values.customMarketBasedMultiplier) ? 'own' : 'precreated'))
    )
    const [lastCustomMarketBasedMultiplier, setLastCustomMarketBasedMultiplier] = useState<string | number | null>(
      hasSupplierRate(formik.values.customMarketBasedMultiplier) ? formik.values.customMarketBasedMultiplier : null
    )
    const [lastEnergySupplierTariffId, setLastEnergySupplierTariffId] = useState<string | null>(
      formik.values.energySupplierTariffId || null
    )
    // Returns true if the field should render above the Advanced collapse (mandatory placement)
    const isMandatory = (key: string) => mandatoryFields.includes(key);

    // Fields the parent form owns but wants collected here rather than above the fold
    // (the subsidiary and the conversion factor source: both defaulted, rarely changed,
    // and org-level rather than part of the record being written).
    const hasLeadingFields = Boolean(leadingFields)

    const hasAdvancedContent = (
      hasLeadingFields ||
      (!hideCountry && !isMandatory('country')) ||
      (!hideLocation && !isMandatory('location')) ||
      (!hideSupplier && !isMandatory('supplier')) ||
      (!hideVehicle && !isMandatory('vehicle')) ||
      (!hideProduct && !isMandatory('product')) ||
      (!hideProject && !isMandatory('project')) ||
      (!hideCustomBasedMarketMultiplier && !isMandatory('energySupplierTariff')) ||
      !hideRec ||
      (!hideCustomFields && customFields.some(cf => !isMandatory(cf.id))) ||
      (!hideRegulatedEmissionTradingScheme && !isMandatory('ets')) ||
      (!hideExcludedFromReporting && !isMandatory('excludeFromReporting'))
    )

    useEffect(() => {
      if (!hideVehicle) {
        vehicleService.getAllActiveVehicles()
          .then(response => setVehicles(response))
          .catch(err => console.log(err))
      }
      if (!hideProduct) {
        capitalGoodService.getAllActive()
          .then(response => setCapitalGoods(response))
          .catch(err => console.log(err))
      }
      if (!hideLocation) {
        locationService.getActiveLocations()
          .then(response => setLocations(response))
          .catch(err => console.log(err))
      }
      if (!hideCountry) {
        locationService.getAllCountries()
          .then(response => setCountries(response))
          .catch(err => console.log(err))
      }
      if (!hideRegulatedEmissionTradingScheme) {
        regulatedEmissionTradingScheme.getAll()
          .then(response => setSchemes(response))
          .catch(err => console.log(err))
      }
      if (!hideProject) {
        projectService.getAllActive()
          .then(response => setProjects(response))
          .catch(err => console.log(err))
      }
      if (!hideSupplier) {
        contactSupplierService.getActiveSuppliersLite(false)
          .then(response => setContactSuppliers(response))
          .catch(err => console.log(err))
      }
      if (!hideRec) {
        if (!isEdit) {
          renewableEnergyCertificateService.getAllActive()
            .then(response => setRenewableEnergyCertificates(response))
            .catch(err => console.log(err))
        } else {
          renewableEnergyCertificateService.getAll()
            .then(response => setRenewableEnergyCertificates(response))
            .catch(err => console.log(err))
        }
      }
      if (!hideCustomBasedMarketMultiplier || !hideEnergySupplierTariff) {
        energySupplierTariffService.getAllActive()
          .then(response => setEnergySupplierTariffs(response))
          .catch(err => console.log(err))
      }
    }, [hideVehicle, hideProduct, hideLocation, hideCountry, hideRegulatedEmissionTradingScheme, hideProject, hideSupplier, hideRec])


    useEffect(() => {
      if (formik.values.partOfEts) {
        setShowEts(true)
      }
    }, [])

    useEffect(() => {
      if (!hideSupplier && defaultContactSupplierId && !formik.values.contactSupplierId) {
        formik.setFieldValue('contactSupplierId', defaultContactSupplierId);
      }
    }, [defaultContactSupplierId, hideSupplier])

    useEffect(() => {
      if (defaultEnergySupplierTariffId && !formik.values.energySupplierTariffId) {
        formik.setFieldValue('energySupplierTariffId', defaultEnergySupplierTariffId);
      }
    }, [defaultEnergySupplierTariffId])

    useEffect(() => {
      if (showOnlyCustomBasedMarketMultiplier) {
        setElectricitySupplierMode('own');
        if (formik.values.energySupplierTariffId) {
          formik.setFieldValue('energySupplierTariffId', '');
        }
      }
    }, [showOnlyCustomBasedMarketMultiplier, formik])

    const handleElectricitySupplierModeChange = (_e: any, val: 'precreated' | 'own' | null) => {
      if (!val) return;
      setElectricitySupplierMode(val);
      if (val === 'own') {
        if (formik.values.energySupplierTariffId) {
          setLastEnergySupplierTariffId(formik.values.energySupplierTariffId)
        }
        formik.setFieldValue('energySupplierTariffId', '')
        if ((formik.values.customMarketBasedMultiplier === null || formik.values.customMarketBasedMultiplier === '') && lastCustomMarketBasedMultiplier !== null && lastCustomMarketBasedMultiplier !== undefined) {
          formik.setFieldValue('customMarketBasedMultiplier', lastCustomMarketBasedMultiplier)
        }
      }
      if (val === 'precreated') {
        if (formik.values.customMarketBasedMultiplier !== null && formik.values.customMarketBasedMultiplier !== '') {
          setLastCustomMarketBasedMultiplier(formik.values.customMarketBasedMultiplier)
        }
        formik.setFieldValue('customMarketBasedMultiplier', null)
        if ((!formik.values.energySupplierTariffId || formik.values.energySupplierTariffId === '') && lastEnergySupplierTariffId) {
          formik.setFieldValue('energySupplierTariffId', lastEnergySupplierTariffId)
        }
      }
    }

    const selectCountryHandler = (e: SelectChangeEvent<ICountry | null>, value: ICountry | null) => {
      if (value) {
        formik.setValues({ ...formik.values, countryId: value.id })
      }
    }

    const partOfEtsHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === 'true';
      formik.setFieldValue('partOfEts', value)
      if (!value) {
        formik.setFieldValue('regulatedEmissionTradingSchemeId', null)
      }
      setShowEts(value);
    };

    const excludeFromReportingHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === 'true';
      formik.setFieldValue('excludeFromReporting', value)
      if (!value) {
        formik.setFieldValue('excludeFromReportingReason', '')
      }
    };

    const selectLocationHandler = (value: ILocation | null) => {
      const locationId = value?.id || ''
      const locationRate = value?.locationMarketBasedConversions?.[0]?.customMarketBasedMultiplier
      formik.setFieldValue('locationId', locationId)
      if (value) {
        if (!hideCustomBasedMarketMultiplier) {
          if (value.energySupplierTariffId) {
            formik.setFieldValue('energySupplierTariffId', value.energySupplierTariffId)
            formik.setFieldValue('customMarketBasedMultiplier', null)
            setLastCustomMarketBasedMultiplier(null)
            setElectricitySupplierMode('precreated')
          }
          else if (hasSupplierRate(locationRate)) {
            formik.setFieldValue('customMarketBasedMultiplier', locationRate)
            formik.setFieldValue('energySupplierTariffId', null)
            setLastEnergySupplierTariffId(null)
            setElectricitySupplierMode('own')
          }
          else {
            formik.setFieldValue('customMarketBasedMultiplier', null)
            formik.setFieldValue('energySupplierTariffId', null)
          }
        } else {
          formik.setFieldValue('customMarketBasedMultiplier', null)
        }
        if (formik.values.groupId === 'acf9ae04-25d9-43d7-b442-fabd69e5844b' && formik.values.categoryId === 'df026bd9-281d-4013-80e4-a1ef92965502' && value.electricitySupplierId) {
          formik.setFieldValue('contactSupplierId', value.electricitySupplierId)
        }
      }
    }

    // Renders a single field JSX, used in both mandatory and advanced sections
    const renderCountry = () => !hideCountry && countries ? (
      <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
        <Autocomplete
          id="countryId"
          options={countries.slice().sort((a, b) => (a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name)))}
          sx={{ color: 'text.primary' }}
          getOptionLabel={(option) => `${option.name}`}
          renderOption={(props, option) => {
            const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
            return (
              <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} key={key} {...restProps}>
                <img loading="lazy" width="20" src={option.imageUrl} alt="country flag" />
                {option.name}
              </Box>
            );
          }}
          value={countries?.find(country => country.id === formik.values.countryId) || null}
          onChange={(e: any, value: ICountry | null) => selectCountryHandler(e, value)}
          renderInput={(params) => (
            <TextField label='Country' {...params} error={formik.touched.countryId && Boolean(formik.errors.countryId)} />
          )}
        />
        {formik.touched.countryId && formik.errors.countryId &&
          <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
            {formik.errors.countryId ? <div>{formik.errors.countryId as string}</div> : null}
          </FormHelperText>}
      </FormControl>
    ) : null;

    return (
      <>
        {/* Mandatory fields — rendered above FLAG and Advanced collapse */}
        {isMandatory('country') && renderCountry()}
        {isMandatory('location') && !hideLocation && locations && (
          <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
            <Autocomplete
              id="locationId"
              options={locations.slice().sort((a, b) => a.friendlyName.localeCompare(b.friendlyName))}
              sx={{ color: 'text.primary' }}
              getOptionLabel={(option) => option.friendlyName}
              value={locations?.find(location => location.id === formik.values.locationId) || null}
              onChange={(e: any, value: ILocation | null) => selectLocationHandler(value)}
              renderInput={(params) => (
                <TextField label={screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })} {...params} error={formik.touched.locationId && Boolean(formik.errors.locationId)} />
              )}
            />
            {formik.touched.locationId && formik.errors.locationId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.locationId ? <div>{formik.errors.locationId as string}</div> : null}
              </FormHelperText>}
          </FormControl>
        )}
        {isMandatory('supplier') && !hideSupplier && contactSuppliers && (
          <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
            <SupplierPicker
              id="contactSupplierId"
              suppliers={contactSuppliers}
              sx={{ color: 'text.primary' }}
              value={formik.values.contactSupplierId}
              onChange={(id) => { formik.setFieldValue('contactSupplierId', id || ''); }}
              error={formik.touched.contactSupplierId && Boolean(formik.errors.contactSupplierId)}
            />
            {formik.touched.contactSupplierId && formik.errors.contactSupplierId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.contactSupplierId ? <div>{formik.errors.contactSupplierId as string}</div> : null}
              </FormHelperText>}
          </FormControl>
        )}
        {isMandatory('vehicle') && !hideVehicle && vehicles && (
          <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
            <Autocomplete
              id="vehicleId"
              options={vehicles.slice().sort((a, b) => a.customUniqueId.localeCompare(b.customUniqueId))}
              sx={{ color: 'text.primary' }}
              getOptionLabel={(option) => option.customUniqueId}
              value={vehicles?.find(vehicle => vehicle.id === formik.values.vehicleId) || null}
              onChange={(e: any, value: IVehicleDetails | null) => { formik.setFieldValue('vehicleId', value?.id || ''); }}
              renderInput={(params) => (
                <TextField label={screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} {...params} error={formik.touched.vehicleId && Boolean(formik.errors.vehicleId)} />
              )}
            />
            {formik.touched.vehicleId && formik.errors.vehicleId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.vehicleId ? <div>{formik.errors.vehicleId as string}</div> : null}
              </FormHelperText>}
          </FormControl>
        )}
        {isMandatory('product') && !hideProduct && capitalGoods && (
          <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
            <Autocomplete
              id="capitalGoodId"
              options={capitalGoods.slice().sort((a, b) => a.name.localeCompare(b.name))}
              sx={{ color: 'text.primary' }}
              getOptionLabel={(option) => option.name}
              value={capitalGoods?.find(product => product.id === formik.values.capitalGoodId) || null}
              onChange={(e: any, value: ICapitalGood | null) => { formik.setFieldValue('capitalGoodId', value?.id || ''); }}
              renderInput={(params) => (
                <TextField label={screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })} {...params} error={formik.touched.capitalGoodId && Boolean(formik.errors.capitalGoodId)} />
              )}
            />
            {formik.touched.capitalGoodId && formik.errors.capitalGoodId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.capitalGoodId ? <div>{formik.errors.capitalGoodId as string}</div> : null}
              </FormHelperText>}
          </FormControl>
        )}
        {isMandatory('project') && !hideProject && organisationDetails?.enableProjects && projects && (
          <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
            <Autocomplete
              id="projectId"
              options={projects.slice().sort((a, b) => a.summary.localeCompare(b.summary))}
              sx={{ color: 'text.primary' }}
              getOptionLabel={(option) => option.summary}
              value={projects?.find(project => project.id === formik.values.projectId) || null}
              onChange={(e: any, value: IProject | null) => { formik.setFieldValue('projectId', value?.id || ''); }}
              renderInput={(params) => (
                <TextField label={screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })} {...params} error={formik.touched.projectId && Boolean(formik.errors.projectId)} />
              )}
            />
            {formik.touched.projectId && formik.errors.projectId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.projectId ? <div>{formik.errors.projectId as string}</div> : null}
              </FormHelperText>}
          </FormControl>
        )}
        {isMandatory('ets') && !hideRegulatedEmissionTradingScheme && (
          <>
            <FormControl>
              <Typography variant='body1' sx={{ color: 'text.primary', mt: 1 }}>Are these emissions part of a Regulated Emission Trading Scheme </Typography>
              <RadioGroup onChange={partOfEtsHandler} value={formik.values.partOfEts} sx={{ width: '100%', display: 'flex', flexDirection: 'row', color: 'text.primary', alignContent: 'center', justifyContent: 'start' }}>
                <FormControlLabel value={false} control={<Radio />} label='No' />
                <FormControlLabel value={true} control={<Radio />} label='Yes' />
              </RadioGroup>
            </FormControl>
            {showEts && (
              <FormControl fullWidth variant='outlined' sx={{ mt: 1 }}>
                <InputLabel htmlFor='regulatedEmissionTradingSchemeId'>Regulated Emission Trading Scheme</InputLabel>
                <Select id='regulatedEmissionTradingSchemeId' label='Regulated Emission Trading Scheme' name='regulatedEmissionTradingSchemeId' value={formik.values.regulatedEmissionTradingSchemeId || ''} onChange={formik.handleChange} error={formik.touched.regulatedEmissionTradingSchemeId && Boolean(formik.errors.regulatedEmissionTradingSchemeId)} sx={{ color: 'text.primary' }}>
                  {schemes?.map((scheme, index) => <MenuItem key={index} value={scheme.id}>{scheme.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </>
        )}
        {isMandatory('excludeFromReporting') && !hideExcludedFromReporting && (
          <>
            <FormControl>
              <Typography variant='body1' sx={{ color: 'text.primary', mt: 1 }}>Do you wish to exclude these emissions from analytics and reporting? </Typography>
              <RadioGroup onChange={excludeFromReportingHandler} value={formik.values.excludeFromReporting} sx={{ width: '100%', display: 'flex', flexDirection: 'row', color: 'text.primary', alignContent: 'center', justifyContent: 'start' }}>
                <FormControlLabel value={true} control={<Radio />} label='Yes' />
                <FormControlLabel value={false} control={<Radio />} label='No' />
              </RadioGroup>
            </FormControl>
            {formik.values.excludeFromReporting && (
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='excludeFromReportingReason'>Reason for exclusion</InputLabel>
                <OutlinedInput id='excludeFromReportingReason' label='Reason for exclusion' name='excludeFromReportingReason' value={formik.values.excludeFromReportingReason || ''} onChange={formik.handleChange} error={formik.touched.excludeFromReportingReason && Boolean(formik.errors.excludeFromReportingReason)} sx={{ color: 'text.primary' }} />
              </FormControl>
            )}
          </>
        )}
        {/* Mandatory custom fields */}
        {!hideCustomFields && customFields && customFields.filter(cf => isMandatory(cf.id)).map((customField, index) => (
          <FormControl key={`mandatory-cf-${index}`} fullWidth variant='outlined' sx={{ mb: 2 }}>
            <InputLabel htmlFor={customField.id}>{customField.fieldName}</InputLabel>
            <OutlinedInput id={customField.id} label={customField.fieldName} name={customField.id} type={customField.fieldType === 'number' ? 'number' : 'text'} value={formik.values[customField.id] ?? ''} onChange={formik.handleChange} error={formik.touched[customField.id] && Boolean(formik.errors[customField.id])} sx={{ color: 'text.primary' }} />
            {formik.touched[customField.id] && formik.errors[customField.id] && (
              <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>
                {formik.errors[customField.id] as string}
              </FormHelperText>
            )}
          </FormControl>
        ))}

        {organisationDetails?.isFlagEnabled && (
          /* One decision, one control, and no section heading above it: every other field on
             this form carries its own label and nothing else, and "FLAG classification" over a
             field labelled "FLAG category" was saying it twice. The checkbox that used to sit
             here repeated what choosing a category already says, and unticking it left the
             category behind: the two fields are written independently, so that stored a row
             carrying a category while excluded from the FLAG report. */
          <Stack sx={{ mt: 2, mb: 2 }}>
            <FormControl fullWidth variant='outlined'>
              <InputLabel htmlFor='flagCategory'>FLAG category</InputLabel>
              <Select
                id='flagCategory'
                label='FLAG category'
                name='flagCategory'
                value={flagSelectValueFrom(formik.values.isFlag, formik.values.flagCategory)}
                onChange={(event) => {
                  const fields = flagFieldsFromSelectValue(event.target.value as string);
                  formik.setFieldValue('isFlag', fields.isFlag);
                  formik.setFieldValue('flagCategory', fields.flagCategory);
                }}
                sx={{ color: 'text.primary' }}
              >
                <MenuItem value=''>Not FLAG-related</MenuItem>
                {flagSelectValueFrom(formik.values.isFlag, formik.values.flagCategory) === FLAG_NO_CATEGORY_VALUE && (
                  <MenuItem value={FLAG_NO_CATEGORY_VALUE}>FLAG-related, no category recorded</MenuItem>
                )}
                {FLAG_CATEGORY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}
        {hasAdvancedContent && (
          <Stack direction='row' sx={{ pt: 2, pb: 2 }}>
            <Box onClick={() => setViewAdvanced(!viewAdvanced)} sx={{ flexBasis: '200px', textDecoration: 'none', cursor: 'pointer' }}>
              <Stack direction='row'>
                {viewAdvanced ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                <Typography>Advanced</Typography>
              </Stack>
            </Box>
          </Stack>
        )}
        {hasAdvancedContent && viewAdvanced && <>
          {leadingFields}
          {!hideCountry && !isMandatory('country') && countries && <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
            <Autocomplete
              id="countryId"
              options={countries.slice().sort((a, b) => (a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name)))}
              sx={{
                color: 'text.primary'
              }}
              getOptionLabel={(option) => `${option.name}`}
              renderOption={(props, option) => {
                const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                return (
                  <Box
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    key={key}
                    {...restProps}
                  >
                    <img
                      loading="lazy"
                      width="20"
                      src={option.imageUrl}
                      alt="country flag"
                    />
                    {option.name}
                  </Box>
                )
              }}
              value={countries?.find(country => country.id === formik.values.countryId) || null}
              onChange={(e: any, value: ICountry | null) => selectCountryHandler(e, value)}
              renderInput={(params) => (
                <TextField
                  label='Country'
                  {...params}
                  error={formik.touched.countryId && Boolean(formik.errors.countryId)}
                />
              )}
            />
            {formik.touched.countryId && formik.errors.countryId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.countryId ? <div>{formik.errors.countryId as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>}
          {!hideLocation && !isMandatory('location') && locations &&
            <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
              <Autocomplete
                id="locationId"
                options={locations.slice().sort((a, b) => a.friendlyName.localeCompare(b.friendlyName))}
                sx={{
                  color: 'text.primary'
                }}
                getOptionLabel={(option) => option.friendlyName}
                value={locations?.find(location => location.id === formik.values.locationId) || null}
                onChange={(e: any, value: ILocation | null) => selectLocationHandler(value)}
                renderInput={(params) => (
                  <TextField
                    label={screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })}
                    {...params}
                    error={formik.touched.locationId && Boolean(formik.errors.locationId)}
                  />
                )}
              />
              {formik.touched.locationId && formik.errors.locationId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.locationId ? <div>{formik.errors.locationId as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {!hideSupplier && !isMandatory('supplier') && contactSuppliers &&
            <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
              <SupplierPicker
                id="contactSupplierId"
                suppliers={contactSuppliers}
                sx={{
                  color: 'text.primary'
                }}
                value={formik.values.contactSupplierId}
                onChange={(id) => {
                  formik.setFieldValue('contactSupplierId', id || '');
                }}
                error={formik.touched.contactSupplierId && Boolean(formik.errors.contactSupplierId)}
              />
              {formik.touched.contactSupplierId && formik.errors.contactSupplierId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.contactSupplierId ? <div>{formik.errors.contactSupplierId as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {!hideProduct && !isMandatory('product') && capitalGoods &&
            <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
              <Autocomplete
                id="capitalGoodId"
                options={capitalGoods.slice().sort((a, b) => a.name.localeCompare(b.name))}
                sx={{
                  color: 'text.primary'
                }}
                getOptionLabel={(option) => option.name}
                value={capitalGoods?.find(product => product.id === formik.values.capitalGoodId) || null}
                onChange={(e: any, value: ICapitalGood | null) => {
                  formik.setFieldValue('capitalGoodId', value?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    label={screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })}
                    {...params}
                    error={formik.touched.capitalGoodId && Boolean(formik.errors.capitalGoodId)}
                  />
                )}
              />
              {formik.touched.capitalGoodId && formik.errors.capitalGoodId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.capitalGoodId ? <div>{formik.errors.capitalGoodId as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {!hideCustomBasedMarketMultiplier && <>
            {!showOnlyCustomBasedMarketMultiplier && <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup
                exclusive
                size='small'
                value={electricitySupplierMode}
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none'
                  }
                }}
                onChange={handleElectricitySupplierModeChange}
              >
                <ToggleButton value='precreated' sx={{ textTransform: 'none' }}>Standard Tariff</ToggleButton>
                <ToggleButton value='own' sx={{ textTransform: 'none' }}>Custom Tariff</ToggleButton>
              </ToggleButtonGroup>
            </Box>}
            {!hideEnergySupplierTariff && !showOnlyCustomBasedMarketMultiplier && electricitySupplierMode === 'precreated' && energySupplierTariffs &&
              <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
                <Autocomplete
                  id="energySupplierTariffId"
                  options={energySupplierTariffs.slice().sort((a, b) => {
                    const supplierCompare = a.energySupplier.name.localeCompare(b.energySupplier.name);
                    if (supplierCompare !== 0) return supplierCompare;
                    return a.name.localeCompare(b.name);
                  })}
                  sx={{
                    color: 'text.primary'
                  }}
                  groupBy={(option) => option.energySupplier.name}
                  getOptionLabel={(option) => `${option.name} (${option.energySupplier.name})`}
                  value={energySupplierTariffs?.find(tariff => tariff.id === formik.values.energySupplierTariffId) || null}
                  onChange={(e: any, value: IEnergySupplierTariff | null) => {
                    formik.setFieldValue('energySupplierTariffId', value?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      label='Energy Supplier Tariff'
                      {...params}
                      error={formik.touched.energySupplierTariffId && Boolean(formik.errors.energySupplierTariffId)}
                    />
                  )}
                />
                {formik.touched.energySupplierTariffId && formik.errors.energySupplierTariffId &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.energySupplierTariffId ? <div>{formik.errors.energySupplierTariffId as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>
            }
            {(showOnlyCustomBasedMarketMultiplier || electricitySupplierMode === 'own') &&
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Supplier Emission Rate"
                  name="customMarketBasedMultiplier"
                  type="text"
                  value={formik.values.customMarketBasedMultiplier ?? ''}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.customMarketBasedMultiplier &&
                    Boolean(formik.errors.customMarketBasedMultiplier)
                  }
                  InputProps={{
                    inputProps: { min: 0, inputMode: 'decimal' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          kgCO₂/kWh
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ color: 'text.primary' }}
                />
                {formik.touched.customMarketBasedMultiplier && formik.errors.customMarketBasedMultiplier &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.customMarketBasedMultiplier ? <div>{formik.errors.customMarketBasedMultiplier as string}</div> : null}
                  </FormHelperText>
                }
              </Stack>
            }
          </>}
          {!hideRec && renewableEnergyCertificates && organisationDetails?.enableRecs &&
            <Stack>
              <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
                <InputLabel htmlFor='renewableEnergyCertificateIds'>Renewable Energy Certificate</InputLabel>
                <Select
                  id='renewableEnergyCertificateIds'
                  label='Renewable Energy Certificate'
                  name='renewableEnergyCertificateIds'
                  multiple
                  value={formik.values.renewableEnergyCertificateIds || []}
                  onChange={(e) => formik.setFieldValue('renewableEnergyCertificateIds', e.target.value)}
                  renderValue={(selected) => {
                    const selectedIds = (selected as string[]) || [];
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedIds.map((id) => {
                          const rec = renewableEnergyCertificates?.find(r => r.id === id);
                          const label = rec ? `${rec.certificateNumber}` : id;
                          return <Chip key={id} label={label} />
                        })}
                      </Box>
                    );
                  }}
                  error={formik.touched.renewableEnergyCertificateIds && Boolean(formik.errors.renewableEnergyCertificateIds)}
                  sx={{ color: 'text.primary', }}>
                  {(renewableEnergyCertificates
                    .slice()
                    .sort((a, b) => a.certificateNumber.localeCompare(b.certificateNumber))
                    .filter((rec) => {
                      // Only offer certificates whose type matches the emission: RGGO for gas,
                      // REC/REGO/GoO (and any legacy/missing type) for electricity.
                      const isRggo = rec.certificateType === 'RGGO';
                      if (recTypeContext === 'gas' ? !isRggo : isRggo) return false;
                      const selectedIds = formik.values.renewableEnergyCertificateIds || [];
                      if (!isEdit) return true;
                      return !rec.retired || selectedIds.includes(rec.id);
                    })
                  ).map((renewableEnergyCertificate, index) => {
                    const selectedIds = formik.values.renewableEnergyCertificateIds || [];
                    const isChecked = selectedIds.indexOf(renewableEnergyCertificate.id) > -1;
                    const label = `Certificate ${renewableEnergyCertificate.certificateNumber}${renewableEnergyCertificate.retired ? ' (retired)' : ''}`;
                    return (
                      <MenuItem key={index} value={renewableEnergyCertificate.id}>
                        <Checkbox checked={isChecked} sx={{ mr: 1 }} />
                        <ListItemText primary={label} />
                      </MenuItem>
                    )
                  })}
                </Select>
                {formik.touched.renewableEnergyCertificateIds && formik.errors.renewableEnergyCertificateIds &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.renewableEnergyCertificateIds ? <div>{formik.errors.renewableEnergyCertificateIds as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>
            </Stack>
          }
          {!hideVehicle && !isMandatory('vehicle') && vehicles &&
            <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
              <Autocomplete
                id="vehicleId"
                options={vehicles.slice().sort((a, b) => a.customUniqueId.localeCompare(b.customUniqueId))}
                sx={{
                  color: 'text.primary'
                }}
                getOptionLabel={(option) => option.customUniqueId}
                value={vehicles?.find(vehicle => vehicle.id === formik.values.vehicleId) || null}
                onChange={(e: any, value: IVehicleDetails | null) => {
                  formik.setFieldValue('vehicleId', value?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    label={screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })}
                    {...params}
                    error={formik.touched.vehicleId && Boolean(formik.errors.vehicleId)}
                  />
                )}
              />
              {formik.touched.vehicleId && formik.errors.vehicleId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.vehicleId ? <div>{formik.errors.vehicleId as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>}
          {!hideProject && !isMandatory('project') && organisationDetails?.enableProjects && projects &&
            <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
              <Autocomplete
                id="projectId"
                options={projects.slice().sort((a, b) => a.summary.localeCompare(b.summary))}
                sx={{
                  color: 'text.primary'
                }}
                getOptionLabel={(option) => option.summary}
                value={projects?.find(project => project.id === formik.values.projectId) || null}
                onChange={(e: any, value: IProject | null) => {
                  formik.setFieldValue('projectId', value?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    label={screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })}
                    {...params}
                    error={formik.touched.projectId && Boolean(formik.errors.projectId)}
                  />
                )}
              />
              {formik.touched.projectId && formik.errors.projectId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.projectId ? <div>{formik.errors.projectId as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {!hideCustomFields && customFields && customFields.filter(cf => !isMandatory(cf.id)).length > 0 && customFields.filter(cf => !isMandatory(cf.id)).map((customField, index) => (
            <FormControl key={index} fullWidth variant='outlined' sx={{ mb: 2 }}>
              <InputLabel htmlFor={customField.id}>{customField.fieldName}</InputLabel>
              <OutlinedInput id={customField.id} label={customField.fieldName} name={customField.id}
                type={customField.fieldType === 'number' ? 'number' : 'text'}
                value={formik.values[customField.id] ?? ''}
                onChange={formik.handleChange}
                error={formik.touched[customField.id] && Boolean(formik.errors[customField.id])}
                sx={{ color: 'text.primary' }} />
              {formik.touched[customField.id] && formik.errors[customField.id] && (
                <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>
                  {formik.errors[customField.id] as string}
                </FormHelperText>
              )}
            </FormControl>
          ))
          }
          {!hideRegulatedEmissionTradingScheme && !isMandatory('ets') && <>
            <FormControl>
              <Typography variant='body1' sx={{ color: 'text.primary', mt: 1 }}>Are these emissions part of a Regulated Emission Trading Scheme </Typography>
              <RadioGroup
                onChange={partOfEtsHandler}
                value={formik.values.partOfEts}
                sx={{
                  width: '100%',
                  display: 'flex', flexDirection: 'row', color: 'text.primary', alignContent: 'center', justifyContent: 'start'
                }}>
                <FormControlLabel value={false} control={<Radio />} label='No' />
                <FormControlLabel value={true} control={<Radio />} label='Yes' />
              </RadioGroup>
            </FormControl>
            {showEts &&
              <FormControl fullWidth variant='outlined' sx={{ mt: 1 }}>
                <InputLabel htmlFor='regulatedEmissionTradingSchemeId'>Regulated Emission Trading Scheme</InputLabel>
                <Select
                  id='regulatedEmissionTradingSchemeId' label='Regulated Emission Trading Scheme' name='regulatedEmissionTradingSchemeId'
                  value={formik.values.regulatedEmissionTradingSchemeId || ''}
                  onChange={formik.handleChange}
                  error={formik.touched.regulatedEmissionTradingSchemeId && Boolean(formik.errors.regulatedEmissionTradingSchemeId)}
                  sx={{ color: 'text.primary', }}>
                  {schemes?.map((scheme, index) =>
                    <MenuItem key={index} value={scheme.id}>{scheme.name}</MenuItem>
                  )}
                </Select>
                {formik.touched.regulatedEmissionTradingSchemeId && formik.errors.regulatedEmissionTradingSchemeId &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.regulatedEmissionTradingSchemeId ? <div>{formik.errors.regulatedEmissionTradingSchemeId as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>
            }
          </>}
          {!hideExcludedFromReporting && !isMandatory('excludeFromReporting') &&
            <>
              <FormControl>
                <Typography variant='body1' sx={{ color: 'text.primary', mt: 1 }}>Do you wish to exclude these emissions from analytics and reporting? </Typography>
                <RadioGroup
                  onChange={excludeFromReportingHandler}
                  value={formik.values.excludeFromReporting}
                  sx={{
                    width: '100%',
                    display: 'flex', flexDirection: 'row', color: 'text.primary', alignContent: 'center', justifyContent: 'start'
                  }}>
                  <FormControlLabel value={true} control={<Radio />} label='Yes' />
                  <FormControlLabel value={false} control={<Radio />} label='No' />
                </RadioGroup>
              </FormControl>
              {formik.values.excludeFromReporting &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='excludeFromReportingReason'>Reason for exclusion</InputLabel>
                  <OutlinedInput
                    id='excludeFromReportingReason'
                    label='Reason for exclusion'
                    name='excludeFromReportingReason'
                    value={formik.values.excludeFromReportingReason || ''}
                    onChange={formik.handleChange}
                    error={formik.touched.excludeFromReportingReason && Boolean(formik.errors.excludeFromReportingReason)}
                    sx={{ color: 'text.primary' }}
                  />
                  {formik.touched.excludeFromReportingReason && formik.errors.excludeFromReportingReason &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.excludeFromReportingReason ? <div>{formik.errors.excludeFromReportingReason as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
            </>
          }
        </>}
      </>
    )
  }